import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";

import { SD_MENU, SERVICE_DESK_BASE } from "./config";
import { getSettings, type Appearance } from "./services/serviceDeskService";
import { SampleViewBar } from "./components/SampleViewBar";
import { useAccess } from "../../hooks/useAccess";

// Pseudo menu id for the "open the public portal" link (opens in a new tab; it
// is not a /service-desk route).
const PUBLIC_SITE_PATH = "/soporte";

// Shell for the Service Desk agent workspace. Mirrors NuvexAccessLayout: Header
// + AppSidebarMenu (flat menu) + AppsSidebar + the matched section via Outlet.
export default function ServiceDeskLayout() {
  const { t } = useTranslation("serviceDesk");
  const { canAccessSectionOfApp } = useAccess();
  const { t: tCommon } = useTranslation("common");
  const { themeMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } =
    useSessionCheck({ warningThreshold: 60 * 60 * 1000, autoRefresh: true });

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [appearance, setAppearance] = useState<Appearance>({});

  useEffect(() => { getSettings().then((s) => setAppearance(s.appearance || {})).catch(() => {}); }, []);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  const menuItems = [
    ...SD_MENU.filter((item) =>
      canAccessSectionOfApp("service-desk", item.id),
    ).map((item) => ({
      id: item.id,
      label: t(`menu.${item.id}`),
      icon: item.icon,
      path: `${SERVICE_DESK_BASE}/${item.id}`,
    })),
    // Opens the public client portal in a new tab (handled in onNavigate).
    { id: "public-site", label: t("menu.publicSite"), icon: "ri-external-link-line", path: PUBLIC_SITE_PATH },
  ];

  const handleNavigate = (path: string) => {
    if (path === PUBLIC_SITE_PATH) {
      window.open(path, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(path);
  };

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className={`h-screen pt-safe-top flex flex-col bg-app`}>
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>{tCommon("sessionBanner.expiring", { time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header
        title={appearance.headerTitle || t("layout.headerTitle")}
        subtitle={appearance.headerSubtitle || t("layout.headerSubtitle")}
        onOpenAppsSidebar={openAppsSidebar}
        isAppsSidebarOpen={isAppsSidebarOpen}
        />

      <div className="flex flex-1 min-h-0 overflow-hidden relative">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={false}
          onCloseMobileMenu={() => {}}
          onNavigate={handleNavigate}
          isActiveRoute={(itemId) => itemId !== "public-site" && location.pathname.startsWith(`${SERVICE_DESK_BASE}/${itemId}`)}
          footerStats={{
            label: t("layout.footerStats.label"),
            items: [{ label: t("layout.footerStats.sections"), value: SD_MENU.length, className: "text-primary-500" }],
          }}
        />

        <div className="flex-1 max-h-full overflow-auto min-w-0 w-full">
          <SampleViewBar />
          <Outlet />
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId="service-desk"
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
